import axios from "axios";

/**
 * adbox-admin-rails の「端末側編集属性」連携モジュール。
 *
 * 端末表示設定 (レイアウト/テーマ/音声/ログ表示/緯度経度) はこれまで端末の
 * localStorage にのみ保持していたが、 DB (adbox-admin-rails) に正規化した。
 * 設置場所カテゴリ (devicePlaceCategoryId) も同じ経路で端末から編集する。
 *
 *   - 書込 (端末 → Rails): トークン認証 API へ PATCH (saveDeviceSettings)。 表示設定と
 *     設置場所を **1 リクエスト** で送る (端末が複数 API を叩かない方針)。
 *   - 読込 (Rails → 端末): Firestore 一方向配信。 取得/反映 (hydrate) は useDeviceSettings
 *     (deviceLocalSettings) / useTerminal (deviceProfile) が担う。 本モジュールは書込＋検索。
 *
 * 端末は `gateway_id` (= localStorage `terminalId`) で識別する。 認証は共有 Bearer
 * トークン (`VUE_APP_ADBOX_DEVICE_API_TOKEN`、 SPA 埋め込み前提の軽い gating)。 Rails 側
 * `ADBOX_DEVICE_API_TOKEN` と同じ値を env に設定する。 env 未設定時は API を呼ばず、
 * localStorage のみで従来どおり動作する (ローカル開発 / 移行期間)。
 */

/**
 * 表示レイアウトの一覧。**ここが唯一の正本**。
 *
 * 型と実行時の検証を同じ配列から導く。以前は型・localStorage 復元・Firestore 反映の
 * 3 箇所にリテラルを直書きしており、`qr` を足したときに 2 箇所が漏れた。結果、
 * 保存も配信も成功しているのに画面が値を弾いて「保存が効かない」ように見えていた
 * (2026-08-29)。
 *
 * Rails 側の正本は `Delivery::DeviceLocalSetting::DISPLAY_LAYOUTS`。増やすときは両方直す。
 *   plane    … 全画面の動画
 *   tv       … パネルが右辺＋下辺
 *   vertical … 縦型サイネージ
 *
 * QR パネルはレイアウトではなくクリエイティブに付く (計画13: QrWrappedVideo)。
 * 端末単位の `qr` レイアウトは 2026-08-30 に廃止した。
 */
export const DISPLAY_LAYOUTS = ["plane", "tv", "vertical"] as const;
export type DisplayLayout = typeof DISPLAY_LAYOUTS[number];
export const isDisplayLayout = (v: unknown): v is DisplayLayout =>
  DISPLAY_LAYOUTS.includes(v as DisplayLayout);

export const VERTICAL_THEMES = ["light", "dark"] as const;
export type VerticalTheme = typeof VERTICAL_THEMES[number];
export const isVerticalTheme = (v: unknown): v is VerticalTheme =>
  VERTICAL_THEMES.includes(v as VerticalTheme);

export interface DeviceSettings {
  displayLayout: DisplayLayout;
  verticalTheme: VerticalTheme;
  isAudio: boolean;
  isLogVisible: boolean;
}

// 端末選択 検索 (GET /api/devices) の 1 件。 gateway_id / address ＋ 表示設定 ＋
// 設置場所の現在値を同梱する (選択した端末の実値をフォームへ読み込み、 誤上書きを防ぐ)。
export interface DeviceSearchResult extends DeviceSettings {
  gatewayId: string;
  address: string;
  devicePlaceCategoryId: number | null;
}

// 設置場所カテゴリ (トイレ入口 / 受付付近 等) のマスタ。 端末のピッカー用。
export interface PlaceCategory {
  id: number;
  key: string;
  name: string;
}

// GET /api/devices のレスポンス全体。 マスタ一覧は meta に相乗りする
// (端末が別 API を叩かないための設計。 Rails 側 Delivery::Api::DevicesController)。
export interface DeviceSearchResponse {
  devices: DeviceSearchResult[];
  placeCategories: PlaceCategory[];
}

// 端末が書き込む属性。 表示設定 ＋ 設置場所カテゴリを 1 リクエストで送る。
//
// devicePlaceCategoryId は **キーを送ったときだけ** サーバ側が更新する (Rails の部分更新)。
// null は「未設定に戻す」の明示なので、 現在値を把握できていない状態では **キーごと省く**
// こと (省略なら現在値が温存される)。 未確定のまま null を送ると設置場所を消してしまう。
export interface DeviceWritableSettings extends DeviceSettings {
  devicePlaceCategoryId?: number | null;
}

const BASE_URL = process.env.VUE_APP_ADBOX_DEVICE_API_URL;
const TOKEN = process.env.VUE_APP_ADBOX_DEVICE_API_TOKEN;

// API を呼べる状態か (base URL とトークンの両方が設定済み)。 書込 (PATCH) で使う。
export const isDeviceApiConfigured = (): boolean => Boolean(BASE_URL && TOKEN);

const http = axios.create({
  baseURL: BASE_URL,
  timeout: 4000,
  headers: TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {},
});

const settingsPath = (gatewayId: string): string =>
  `/api/devices/${encodeURIComponent(gatewayId)}/settings`;

/**
 * 端末設定を保存 (upsert) する。 成功なら true、 失敗 (端末未登録 / API エラー /
 * env 未設定) なら false。 呼び出し側は localStorage 保存とは独立に結果を扱える。
 * 保存後、 Rails が Firestore へ配信し、 各端末は購読で反映する (一方向)。
 */
export const saveDeviceSettings = async (
  gatewayId: string,
  settings: DeviceSettings | DeviceWritableSettings
): Promise<boolean> => {
  if (!isDeviceApiConfigured() || !gatewayId) return false;
  try {
    await http.patch(settingsPath(gatewayId), settings);
    return true;
  } catch (e) {
    console.warn("[deviceSettings] 保存失敗 (localStorage には保存済み):", e);
    return false;
  }
};

/**
 * 端末を gateway_id の部分一致で検索する (設定画面の端末選択用)。
 * 各結果は表示設定を同梱するので、 選択時に追加リクエスト無しでフォームへ反映できる。
 * 設置場所カテゴリのマスタ (ピッカーの選択肢) も meta で同梱されるため、 この 1 リクエストで
 * 設定画面に必要なものが揃う (端末が複数 API を叩かないための設計)。
 * env 未設定 / API エラー / 短すぎるクエリ (サーバ側で空) は空の結果。
 */
export const searchDevices = async (
  query: string
): Promise<DeviceSearchResponse> => {
  const empty: DeviceSearchResponse = { devices: [], placeCategories: [] };
  if (!isDeviceApiConfigured()) return empty;
  try {
    const res = await http.get("/api/devices", { params: { q: query } });
    return {
      devices: (res.data?.data as DeviceSearchResult[]) ?? [],
      placeCategories:
        (res.data?.meta?.placeCategories as PlaceCategory[]) ?? [],
    };
  } catch (e) {
    console.warn("[deviceSettings] 端末検索に失敗:", e);
    return empty;
  }
};
